import moment from 'moment';
import { YW_CORPORATE_TELEPHONE_NUMBERS, APP_URLS, PSR_REGISTER, PSR_REVIEW } from '../settings/urls';
import { CUSTOMER_REPRESENTATIVE } from '../constants';

export const createToken = (token) => ({ Authorization: `Bearer ${token}` });

export const formatAccountReferenceForRender = (value) => {
  if (!value) {
    return '';
  }
  const accountReference = `${value}`;
  let formattedRef = '';
  for (let i = 0; i < accountReference.length; i++) {
    if (!(i % 4)) {
      formattedRef += ' ';
    }
    formattedRef += `${accountReference[i]}`;
  }
  return formattedRef;
};

export const formatAddressForRender = (addressData) => {
  let addressAsArray = addressData;

  if (!Array.isArray(addressData)) {
    const { addressLineOne, addressLineTwo, addressLineThree, addressLineFour, postcode } = addressData;
    addressAsArray = [addressLineOne, addressLineTwo, addressLineThree, addressLineFour, postcode];
  }

  const valueFilter = (data) => data && data !== '';
  return addressAsArray.filter(valueFilter).join(', ');
};

export const getAge = (dateString) => {
  var today = new Date();
  var birthDate = new Date(dateString);
  var age = today.getFullYear() - birthDate.getFullYear();
  var m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const formatDate = (dateTime) => {
  const d = new Date(dateTime);
  dateTime = d.toLocaleString('en-GB');
  dateTime = dateTime.split(',')[0];
  return moment(dateTime, 'DD/MM/YYYY').format('YYYY-MM-DD');
};

export const formatDateToString = (dateTime) => {
  const d = new Date(dateTime);
  dateTime = d.toLocaleString('en-GB');
  dateTime = dateTime.split(',')[0];
  return moment(dateTime, 'DD/MM/YYYY').format('Do MMMM YYYY');
};

export const formatDay = (date) => {
  const sts = [1, 21, 31];
  const nds = [2, 22];
  const rds = [3, 23];

  if (sts.includes(Number(date))) {
    return `${date}st`;
  }

  if (nds.includes(Number(date))) {
    return `${date}nd`;
  }

  if (rds.includes(Number(date))) {
    return `${date}rd`;
  }

  if (date === 'Last') {
    return date;
  }

  return `${date}th`;
};

export const removeNonDigits = (str) => str.replace(/[^0-9]/g, '');

export const uppercaseFirst = (string) => string.charAt(0).toUpperCase() + string.slice(1);

export const httpErrorHandler = (error) => {
  if (error.errors && error.errors.length > 0) {
    if (typeof error.errors[0] === 'object' && !Array.isArray(error.errors[0])) {
      return error.errors[0];
    } else {
      return `${error.errors[0]}`;
    }
  } else {
    return error.status ? `${error.status}` : '500';
  }
};

export const isSuccessfulEmptyArrayResponse = (response, getItems = (result) => result?.data) => {
  const items = getItems(response);

  return response?.status === 200 && Array.isArray(items) && items.length === 0;
};

export const isCorporateTelephoneNumbers = (value) => {
  const phoneNumber = value.replace(/ /g, '');
  const YWTelephoneNumbers = Object.values(YW_CORPORATE_TELEPHONE_NUMBERS).map((item) => item.replace(/ /g, ''));

  if (YWTelephoneNumbers.includes(phoneNumber)) {
    return true;
  }

  return false;
};

export const isEligibleFirstName = (value) => {
  if (value <= 1) {
    return false;
  }

  return true;
};

export const temporaryChangesDateValid = (value) => {
  const currentDate = new Date();
  const dateSelected = value.split('-'); // Month - Year
  const updatedDateSelected = new Date();
  updatedDateSelected.setMonth(dateSelected[0] - 1);
  updatedDateSelected.setFullYear(dateSelected[1]);

  return currentDate <= updatedDateSelected;
};

export const getDefaultTemporaryChangesDate = () => {
  const currentDate = new Date();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear() + 2;
  return `${month < 10 ? `0${month}` : month}-${year}`;
};

export const handlePushToDataLayer = ({ event, journey, stepName, state, other }) => {
  if (window && window.dataLayer) {
    window.dataLayer.push({
      ...(event && { event }), // The event
      ...(journey && { form_id: `priority_services_${journey.substring(1)}` }), // The form id
      ...(stepName && { form_name: stepName.replace('/', '').replaceAll('-', '_') }), // The name of the step
      ...(state && { form_state: state }), // The state success / failure
      ...other, // Optionally add more properties to the object
    });
  }
};

export const breakAddress = (address) => {
  const parts = address.split(',');

  if (parts.length < 3) {
    throw new Error('Invalid address format');
  }
  const addressLineOne = parts
    .slice(0, -2)
    .join(',')
    .trim();
  const town = parts[parts.length - 2].trim();
  const postcode = parts[parts.length - 1].trim();

  return {
    addressLineOne,
    town,
    postcode,
  };
};

export const getCurrentStepName = (pathname, representative) => {
  switch (pathname) {
    case `${PSR_REGISTER}${APP_URLS.WHAT_YOU_NEED}`:
    case `${PSR_REVIEW}${APP_URLS.WHAT_YOU_NEED}`: {
      return 'You will need';
    }
    case `${PSR_REGISTER}${APP_URLS.CUSTOMER_DETAILS}`:
    case `${PSR_REVIEW}${APP_URLS.CUSTOMER_DETAILS}`: {
      if (representative === CUSTOMER_REPRESENTATIVE.SOMEONE_ELSE) {
        return "Customer's personal details";
      }
      return 'Personal details';
    }
    case `${PSR_REGISTER}${APP_URLS.YOUR_DETAILS}`:
    case `${PSR_REVIEW}${APP_URLS.YOUR_DETAILS}`: {
      return 'Your details';
    }
    case `${PSR_REGISTER}${APP_URLS.SERVICES_REQUIRED}`:
    case `${PSR_REVIEW}${APP_URLS.SERVICES_REQUIRED}`: {
      return 'Service required';
    }
    case `${PSR_REGISTER}${APP_URLS.SECURITY_CODEWORD}`:
    case `${PSR_REVIEW}${APP_URLS.SECURITY_CODEWORD}`: {
      return 'Code word';
    }
    case `${PSR_REGISTER}${APP_URLS.NOMINEE_DETAILS}`:
    case `${PSR_REVIEW}${APP_URLS.NOMINEE_DETAILS}`: {
      return `Nominee's details`;
    }
    case `${PSR_REGISTER}${APP_URLS.YOUR_LANGUAGE}`:
    case `${PSR_REVIEW}${APP_URLS.YOUR_LANGUAGE}`: {
      return 'Preferred language';
    }
    case `${PSR_REGISTER}${APP_URLS.TEMPORARY_LIFE_CHANGES}`:
    case `${PSR_REVIEW}${APP_URLS.TEMPORARY_LIFE_CHANGES}`: {
      return ' Life changes dates';
    }
    case `${PSR_REGISTER}${APP_URLS.TEMPORARY_POST_HOSPITAL_RECOVERY}`:
    case `${PSR_REVIEW}${APP_URLS.TEMPORARY_POST_HOSPITAL_RECOVERY}`: {
      return 'Hospital dates';
    }
    case `${PSR_REGISTER}${APP_URLS.TEMPORARY_YOUNG_ADULT_HOUSEHOLDER}`:
    case `${PSR_REVIEW}${APP_URLS.TEMPORARY_YOUNG_ADULT_HOUSEHOLDER}`: {
      return ' Young adult dates';
    }
    case `${PSR_REGISTER}${APP_URLS.REASONS_REQUIRED}`:
    case `${PSR_REVIEW}${APP_URLS.REASONS_REQUIRED}`: {
      return 'Reason required';
    }
    case `${PSR_REGISTER}${APP_URLS.SUMMARY}`:
    case `${PSR_REVIEW}${APP_URLS.SUMMARY}`: {
      return 'Summary ';
    }
    case `${PSR_REGISTER}${APP_URLS.CONFIRMATION}`:
    case `${PSR_REVIEW}${APP_URLS.CONFIRMATION}`: {
      return 'Submitted ';
    }
    case `${PSR_REGISTER}${APP_URLS.SOMETHING_WENT_WRONG}`:
    case `${PSR_REVIEW}${APP_URLS.SOMETHING_WENT_WRONG}`: {
      return 'Something went wrong';
    }
    case `${PSR_REGISTER}${APP_URLS.CREATE_LOGIN}`:
    case `${PSR_REVIEW}${APP_URLS.CREATE_LOGIN}`: {
      return 'Create Login';
    }
    default:
      return '';
  }
};
